import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { SITES, toXZ, type KanjinSite } from '../kanjinData'

interface Entry {
  site: KanjinSite
  group: THREE.Group
  holder: THREE.Group
  label: THREE.Sprite
  glow: THREE.Mesh
  photo?: THREE.Sprite
}

/** 寄進地マーカー（鳥居/堂のプリミティブ＋ラベル） */
export class SiteMarkers {
  readonly root = new THREE.Group()
  private entries = new Map<number, Entry>()

  build(): void {
    const shu = new THREE.MeshStandardMaterial({ color: 0xa53f2b, roughness: 0.6 })
    const wood = new THREE.MeshStandardMaterial({ color: 0x6b4f2a, roughness: 0.8 })

    for (const site of SITES) {
      const g = new THREE.Group()
      const p = toXZ(site.lat, site.lon)
      g.position.set(p.x, 0.15, p.z)

      // プリミティブは holder に入れる（Meshy製GLBが届いたら enhance() で差し替え）
      const holder = new THREE.Group()
      g.add(holder)

      if (site.kind === 'shrine') {
        // 鳥居
        for (const sx of [-0.9, 0.9]) {
          const post = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 2.4, 6), shu)
          post.position.set(sx, 1.2, 0)
          holder.add(post)
        }
        const kasagi = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.2, 0.26), shu)
        kasagi.position.y = 2.45
        holder.add(kasagi)
        const nuki = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.14, 0.2), shu)
        nuki.position.y = 1.85
        holder.add(nuki)
      } else if (site.kind === 'temple') {
        // 堂（箱＋方形屋根）
        const hall = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.1, 1.4), wood)
        hall.position.y = 0.55
        holder.add(hall)
        const roof = new THREE.Mesh(new THREE.ConeGeometry(1.6, 0.9, 4), new THREE.MeshStandardMaterial({ color: 0x3a332a, roughness: 0.9 }))
        roof.position.y = 1.55
        roof.rotation.y = Math.PI / 4
        holder.add(roof)
      } else {
        // 塚（水城）
        const mound = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.6, 0.8, 8), new THREE.MeshStandardMaterial({ color: 0x5d6b4a, roughness: 1 }))
        mound.position.y = 0.4
        holder.add(mound)
      }

      // 足元グロー
      const glow = new THREE.Mesh(
        new THREE.CircleGeometry(1.9, 24),
        new THREE.MeshBasicMaterial({
          color: 0xffc14d,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      )
      glow.rotation.x = -Math.PI / 2
      glow.position.y = 0.05
      g.add(glow)

      // ラベル
      const label = SiteMarkers.makeLabel(site)
      label.position.set(0, 4.6, 0)
      g.add(label)

      this.root.add(g)
      this.entries.set(site.no, { site, group: g, holder, label, glow })
    }
  }

  /**
   * 追加ビジュアルの遅延読み込み:
   * - Meshy製GLBミニ3Dモデル（sites3d/manifest.json）→ プリミティブと差し替え
   * - Wikipedia代表写真（sites/credits.json）→ 額装プレートをラベル上に表示
   */
  async enhance(base = './assets'): Promise<void> {
    // 写真プレート（近接クラスタは段違い＋左右振り分けで重なりを避け、引き出し線で紐付ける）
    try {
      const credits = (await fetch(`${base}/sites/credits.json`).then((r) => r.json())) as Record<string, unknown>
      const targets = Object.keys(credits)
        .map((s) => this.entries.get(Number(s)))
        .filter((e): e is Entry => !!e && !e.photo)
        .sort((a, b) => a.group.position.x - b.group.position.x) // 西→東
      const placed: { x: number; z: number; tier: number }[] = []
      const lineMat = new THREE.LineBasicMaterial({ color: 0xcbb98a, transparent: true, opacity: 0.75 })

      for (const e of targets) {
        const gx = e.group.position.x
        const gz = e.group.position.z
        // 既配置のうち近い写真と同じ高さを避ける（写真幅~5.4に対し6.5以内を近接とみなす)
        const near = placed.filter((p) => Math.hypot(p.x - gx, p.z - gz) < 6.5)
        let tier = 0
        while (near.some((p) => p.tier === tier)) tier++
        placed.push({ x: gx, z: gz, tier })

        const sprite = await SiteMarkers.makePhotoSprite(`${base}/sites/${e.site.no}.jpg`)
        if (!sprite) continue
        const dx = tier === 0 ? 0 : (tier % 2 === 1 ? 3.4 : -3.4)
        const py = 8.2 + tier * 4.5
        sprite.position.set(dx, py, 0)
        sprite.renderOrder = 5 + tier
        e.group.add(sprite)
        e.photo = sprite

        // 引き出し線: マーカー頂部 → 写真下端
        const pts = [new THREE.Vector3(0, 3.9, 0), new THREE.Vector3(dx, py - 2.0, 0)]
        e.group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), lineMat))
      }
    } catch {
      /* 写真なし */
    }

    // Meshyモデル
    try {
      const manifest = (await fetch(`${base}/sites3d/manifest.json`).then((r) => r.json())) as Record<
        string,
        { file?: string }
      >
      const loader = new GLTFLoader()
      for (const noStr of Object.keys(manifest)) {
        const no = Number(noStr)
        const e = this.entries.get(no)
        const m = manifest[noStr]
        if (!e || !m?.file) continue
        try {
          const gltf = await loader.loadAsync(`${base}/sites3d/${m.file}`)
          const model = gltf.scene
          const bbox = new THREE.Box3().setFromObject(model)
          const size = bbox.getSize(new THREE.Vector3())
          model.scale.setScalar(3.8 / Math.max(size.y, 0.01))
          const b2 = new THREE.Box3().setFromObject(model)
          const c2 = b2.getCenter(new THREE.Vector3())
          model.position.x -= c2.x
          model.position.z -= c2.z
          model.position.y -= b2.min.y
          e.holder.clear()
          e.holder.add(model)
        } catch {
          /* このモデルはスキップ（プリミティブのまま） */
        }
      }
    } catch {
      /* モデル未生成 */
    }
  }

  private static async makePhotoSprite(url: string): Promise<THREE.Sprite | null> {
    try {
      const img = await new Promise<HTMLImageElement>((res, rej) => {
        const i = new Image()
        i.onload = () => res(i)
        i.onerror = rej
        i.src = url
      })
      const c = document.createElement('canvas')
      c.width = 512
      c.height = 384
      const ctx = c.getContext('2d')!
      ctx.fillStyle = '#f4efe2'
      ctx.fillRect(0, 0, 512, 384)
      const dw = 488
      const dh = 348
      const scale = Math.max(dw / img.width, dh / img.height)
      const sw = dw / scale
      const sh = dh / scale
      ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, 12, 12, dw, dh)
      ctx.strokeStyle = '#8a795a'
      ctx.lineWidth = 5
      ctx.strokeRect(6, 6, 500, 372)
      const tex = new THREE.CanvasTexture(c)
      tex.colorSpace = THREE.SRGBColorSpace
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
      sp.scale.set(5.4, 4.05, 1)
      return sp
    } catch {
      return null
    }
  }

  /** マーカー頂部のワールド座標（木片の発射元） */
  topOf(no: number, out: THREE.Vector3): THREE.Vector3 {
    const e = this.entries.get(no)
    if (e) {
      e.group.getWorldPosition(out)
      out.y += 2.6
    }
    return out
  }

  setLabelsVisible(v: boolean): void {
    this.entries.forEach((e) => (e.label.visible = v))
  }

  highlight(no: number, intensity: number): void {
    const e = this.entries.get(no)
    if (!e) return
    ;(e.glow.material as THREE.MeshBasicMaterial).opacity = 0.55 * intensity
    e.glow.scale.setScalar(1 + intensity * 0.6)
  }

  clearHighlights(): void {
    this.entries.forEach((e) => ((e.glow.material as THREE.MeshBasicMaterial).opacity = 0))
  }

  forEach(cb: (e: { site: KanjinSite; pos: THREE.Vector3 }) => void): void {
    const v = new THREE.Vector3()
    this.entries.forEach((e) => {
      e.group.getWorldPosition(v)
      cb({ site: e.site, pos: v.clone() })
    })
  }

  private static makeLabel(site: KanjinSite): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 168
    const ctx = c.getContext('2d')!
    // 木札風の下地（作品トーン: 和紙×朱の縁）
    ctx.fillStyle = 'rgba(12,10,7,0.72)'
    ctx.beginPath()
    ctx.roundRect(4, 4, 504, 160, 10)
    ctx.fill()
    ctx.strokeStyle = 'rgba(165,63,43,0.9)'
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.roundRect(8, 8, 496, 152, 8)
    ctx.stroke()
    ctx.textAlign = 'center'
    // 建物名（読みやすさ優先のゴシック・輪郭付き）
    ctx.font = '600 34px "Yu Gothic UI", sans-serif'
    ctx.lineWidth = 6
    ctx.strokeStyle = 'rgba(0,0,0,0.85)'
    ctx.strokeText(site.building, 256, 52)
    ctx.fillStyle = '#e8eef7'
    ctx.fillText(site.building, 256, 52)
    // 古材名（勧進帳の行書体・金泥）
    ctx.font = '600 58px "HGP行書体", "HG行書体", "HGS行書体", "Yu Mincho", serif'
    ctx.lineWidth = 8
    ctx.strokeText(site.piece, 256, 124)
    ctx.fillStyle = '#ffd77a'
    ctx.fillText(site.piece, 256, 124)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
    sp.scale.set(10.2, 3.35, 1)
    return sp
  }
}
